
// Função para calcular a Páscoa (algoritmo de Gauss)
const calcularPascoa = (ano: number): Date => {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const n = Math.floor((h + l - 7 * m + 114) / 31);
  const p = (h + l - 7 * m + 114) % 31;
  
  return new Date(ano, n - 1, p + 1);
};

// Feriados Nacionais Completos do Brasil
const getFeriadosNacionais = (ano: number): Date[] => {
  const pascoa = calcularPascoa(ano);
  
  // Feriados fixos
  const feriadosFixos = [
    new Date(ano, 0, 1),   // Confraternização Universal
    new Date(ano, 3, 21),  // Tiradentes
    new Date(ano, 4, 1),   // Dia do Trabalho
    new Date(ano, 8, 7),   // Independência do Brasil
    new Date(ano, 9, 12),  // Nossa Senhora Aparecida
    new Date(ano, 10, 2),  // Finados
    new Date(ano, 10, 15), // Proclamação da República
    new Date(ano, 11, 25), // Natal
  ];
  
  // Feriados móveis baseados na Páscoa
  const feriadosMoveis = [
    new Date(pascoa.getTime() - 47 * 24 * 60 * 60 * 1000), // Carnaval (47 dias antes da Páscoa)
    new Date(pascoa.getTime() - 46 * 24 * 60 * 60 * 1000), // Carnaval (46 dias antes da Páscoa)
    new Date(pascoa.getTime() - 2 * 24 * 60 * 60 * 1000),  // Sexta-feira Santa
    pascoa, // Páscoa
    new Date(pascoa.getTime() + 60 * 24 * 60 * 60 * 1000), // Corpus Christi (60 dias após a Páscoa)
  ];
  
  return [...feriadosFixos, ...feriadosMoveis];
};

const isFeriado = (data: Date, feriados: Date[]): boolean => {
  const dataUtc = new Date(Date.UTC(data.getFullYear(), data.getMonth(), data.getDate()));
  return feriados.some(feriado => {
    const feriadoUtc = new Date(Date.UTC(feriado.getFullYear(), feriado.getMonth(), feriado.getDate()));
    return feriadoUtc.getTime() === dataUtc.getTime();
  });
};

export const getDiasUteisMes = (ano: number, mes: number): number => {
  let diasUteis = 0;
  const data = new Date(ano, mes - 1, 1);
  const feriados = getFeriadosNacionais(ano);

  while (data.getMonth() === mes - 1) {
    const diaDaSemana = data.getDay();
    if (diaDaSemana !== 0 && diaDaSemana !== 6) { // Não é Sábado (6) nem Domingo (0)
      if (!isFeriado(data, feriados)) {
        diasUteis++;
      }
    }
    data.setDate(data.getDate() + 1);
  }

  return diasUteis;
};

// Nova função para calcular carga horária mensal considerando 44h semanais
// Segunda a quinta: 9h/dia, Sexta: 8h/dia
export const getCargaHorariaMensal44h = (ano: number, mes: number): number => {
  let cargaHoraria = 0;
  const data = new Date(ano, mes - 1, 1);
  const feriados = getFeriadosNacionais(ano);

  while (data.getMonth() === mes - 1) {
    const diaDaSemana = data.getDay();
    
    // Verificar se é dia útil (segunda a sexta) e não é feriado
    if (diaDaSemana >= 1 && diaDaSemana <= 5 && !isFeriado(data, feriados)) {
      if (diaDaSemana === 5) { // Sexta-feira
        cargaHoraria += 8;
      } else { // Segunda a quinta
        cargaHoraria += 9;
      }
    }
    
    data.setDate(data.getDate() + 1);
  }

  return cargaHoraria;
};

// Função atualizada para gerar períodos considerando a carga horária semanal de 44h
export const gerarPeriodosAno = (ano: number, tipoCalculo: 'tradicional' | 'clt-44h' = 'tradicional', horasDia: number = 8) => {
  const periodos = [];
  
  for (let mes = 0; mes < 12; mes++) {
    let cargaHorariaMes: number;
    
    if (tipoCalculo === 'clt-44h') {
      // Usar o novo cálculo para CLT com 44h semanais
      cargaHorariaMes = getCargaHorariaMensal44h(ano, mes + 1);
    } else {
      // Manter o cálculo tradicional para outros casos
      const diasUteis = getDiasUteisMes(ano, mes + 1);
      cargaHorariaMes = diasUteis * horasDia;
    }
    
    const mesFormatado = String(mes + 1).padStart(2, '0');
    
    periodos.push({
      data_reajuste: `${ano}-${mesFormatado}-01`,
      mes_referencia: `${mesFormatado}/${ano}`,
      carga_horaria_mes: cargaHorariaMes,
    });
  }
  
  return periodos;
};

// Função auxiliar para listar todos os feriados de um ano (útil para debug/verificação)
export const listarFeriadosAno = (ano: number): { nome: string; data: Date }[] => {
  const pascoa = calcularPascoa(ano);
  
  return [
    { nome: 'Confraternização Universal', data: new Date(ano, 0, 1) },
    { nome: 'Carnaval', data: new Date(pascoa.getTime() - 47 * 24 * 60 * 60 * 1000) },
    { nome: 'Carnaval', data: new Date(pascoa.getTime() - 46 * 24 * 60 * 60 * 1000) },
    { nome: 'Tiradentes', data: new Date(ano, 3, 21) },
    { nome: 'Sexta-feira Santa', data: new Date(pascoa.getTime() - 2 * 24 * 60 * 60 * 1000) },
    { nome: 'Páscoa', data: pascoa },
    { nome: 'Dia do Trabalho', data: new Date(ano, 4, 1) },
    { nome: 'Corpus Christi', data: new Date(pascoa.getTime() + 60 * 24 * 60 * 60 * 1000) },
    { nome: 'Independência do Brasil', data: new Date(ano, 8, 7) },
    { nome: 'Nossa Senhora Aparecida', data: new Date(ano, 9, 12) },
    { nome: 'Finados', data: new Date(ano, 10, 2) },
    { nome: 'Proclamação da República', data: new Date(ano, 10, 15) },
    { nome: 'Natal', data: new Date(ano, 11, 25) },
  ];
};
